import { StateContext } from '../../../../GlobalStateStore';
import React, { useContext, useEffect, useState, useRef } from 'react';
import DataSource from '../../../../api';
import { useLocation, useParams } from 'react-router';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import DataState from '../../../../Types/DataState';
import DateCompare, {
  convertDateCompareToTrendMetrics,
} from '../../../../Types/DateCompare';
import { FormattedMessage, useIntl } from 'react-intl';
import TeamProfileContainer from '../LighthouseMetrics/TeamProfileContainer';
import { getDateRange, isDateValid, weeksList } from '../../../../Util';
import useCustomTracking from '../../../../Util/useCustomTracking';
import { EmptyTeamProfile, TeamProfile } from '../../../../Types';
import CSAList from './CSAListSheet';
import { CSASheetRef, CSAListSheetProps } from './CSAListSheet';
import { ActiveCsas, EmptyActiveCsas, Stats } from '../../../../Types/Metric';
import { Profile } from '../../../../Types';
import {
  EmptyFilters,
  getDefaultDateRange,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import TeamView from './TeamView';
import { LighthouseFilters } from '../../../Dashboard/DashboardComponents/LighthouseMetrics/types';
import {
  getMetricListForVertical,
  MetricsOption,
  PresetDate,
  TrendCategory,
  trendMetricsMap,
  VerticalOption,
} from '@amzn/aet-galactus-kit';
import { DIRECT_REPORT_TYPE, TrendAndDetailsMetrics } from './types';
import InputGroup from '@amzn/meridian/input-group';
import Select, { SelectOption } from '@amzn/meridian/select';
import Column from '@amzn/meridian/column';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import styles from './TimeTrendsAndMetricsTeam.module.css';
import { checkGoUser } from '../../../../Util/User';
import { isFeatureEnabled, Feature } from '../../../../config/FeaturesConfig';
import CSAView from './CSAView';
import {
  CachedMetric,
  filterCCXMetricsForRemoval,
  filterCCXNeg,
  filterMetricsAndSubmetrics,
  getReportingTeamForDirectReports,
  getTeamDirectReports,
  getTeamIsTMForDirectReports,
  getTimeOptions,
  getTrendCategoryStringIdAndDefaultMessage,
  ViewByToggle,
} from './helper';
import Filters from '../../FilterComponent/Filter';
import Page from '../../../../Types/Page';
import CSAGraphView from './CSAGraphView';
import TeamGraphView from './TeamGraphView';
import { getMetricStringIdAndDefaultMessage } from '../LighthouseMetrics/helper';
import GoalSettingContainerWrapper from '../../../Goals/GoalSettingContainer';
import { GoalPage } from '../../../Goals/helper';
import BottomQuartile from './BottomQuartile';
import { mapCapAndConsumerToV2ForTrend } from '../../../../Util/Helper';
import { sortVerticalsAndPutTotalFirst } from '../../AssociateRichCards/Util/helper';
import DateRangePicker from '@amzn/meridian/date-range-picker/date-range-picker';

interface DashboardProps {
  profile: Profile;
  profileState: string;
}

const TimeTrendsAndMetricsTeam: React.FC<DashboardProps> = ({
  profile,
  profileState,
}: DashboardProps): React.ReactElement => {
  const enableGoalSetting = isFeatureEnabled(Feature.GOAL_SETTING);
  const enableAssociateRichCards = isFeatureEnabled(
    Feature.ASSOCIATE_RICH_CARDS
  );
  const apiNaws = DataSource.getDataFeed('NAWS');
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const [teamProfileState, setTeamProfileState] = useState(DataState.LOADING);
  const [stats, setStats] = useState<Stats>();
  const [statsState, setStatsState] = useState(DataState.LOADING);
  const TRAILING_DAYS = 'Trailing 7 days';
  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(weekOptions[0]);
  const [teamProfile, setTeamProfile] = useState<TeamProfile>(EmptyTeamProfile);

  // Custom Date Feature for Trends Over Time Metrics: default set to the past 7 days
  const defaultDateRange = getDefaultDateRange();
  const [dateRange, setDateRange] = useState(defaultDateRange);

  const intl = useIntl();
  let { userId } = useParams() as {
    userId: string;
  };

  const {
    userID,
    cscEntityPrefix,
    isTm,
    isOm,
    requesterUserID,
    requesterUsername,
  } = inspireOptions.data;
  userId = userId || userID;
  const directReportType = isOm
    ? DIRECT_REPORT_TYPE.TM
    : DIRECT_REPORT_TYPE.ASSOCIATE;
  const [teamState, setTeamState] = useState(DataState.LOADING);
  const [dateSelection, setDateSelection] = useState(DateCompare.DAY);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [csaListSheetData, setCsaListSheetData] = useState<CSAListSheetProps>({
    csas: [],
    label: '',
    clickstream: {
      sectionType: '',
      linkType: '',
      dateSelection: '',
    },
  });

  const [dateList, setDateList] = useState<string[]>([]);

  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };

  const [trendsMetrics, setTrendsMetrics] = useState<TrendAndDetailsMetrics>();
  const [trendsMetricsState, setTrendsMetricsState] = useState(
    DataState.LOADING
  );
  const [trendsMetricsCache, setTrendsMetricsCache] = useState<CachedMetric[]>(
    []
  );

  const [teamDetailsMetrics, setTeamDetailsMetrics] = useState<
    TrendAndDetailsMetrics[]
  >([]);
  const [teamDetailsMetricsState, setTeamDetailsMetricsState] = useState(
    DataState.LOADING
  );
  const [teamDetailsMetricsCache, setTeamDetailsMetricsCache] = useState<
    CachedMetric[]
  >([]);
  const [metricCategory, setMetricCategory] = useState(TrendCategory.QUALITY);
  const [metricCategoryList, setMetricCategoryList] = useState([
    TrendCategory.QUALITY.toString(),
  ]);
  const [metricType, setMetricType] = useState<string[]>([]); // selected metrics
  const [subMetricType, setSubMetricType] = useState<string[]>([]); // selected submetrics
  const [noSubmetricOptions, setNoSubmetricOptions] = useState(false);
  const [metricTypeOptionsList, setMetricTypeOptionsList] = useState<string[]>(
    []
  ); // complete list of metrics based on category
  const [subMetricTypeOptionsList, setSubMetricTypeOptionsList] = useState<
    string[]
  >([]); // complete list of metrics based on category

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const csaParam = params.get('selectedCSA');
  const verticalParamFromARC = params.get('selectedVertical');

  const tabInference = csaParam ? 'csaview' : 'teamview';

  const [currentTab, setCurrentTab] = useState(tabInference);
  const [viewByType, setViewByType] = useState(ViewByToggle.GRAPH);
  const enableHighSilence = isFeatureEnabled(Feature.HIGH_SILENCE_RATE);

  function getMetricsCacheKey(): string {
    const trendsMetricsDate = convertDateCompareToTrendMetrics(dateSelection);
    return `${verticalValue}|${channelValue}|${skillValue}|${segmentValue}|${marketplaceValue}|${trendsMetricsDate}`;
  }

  const [LHDefaultVertical, setLHDefaultVertical] = useState('');
  const [LHDefaultVerticalState, setLHDefaultVerticalState] = useState(
    DataState.LOADING
  );
  const [activeCsas, setActiveCsas] = useState<ActiveCsas>(EmptyActiveCsas);
  const [activeCsasState, setActiveCsasState] = useState(DataState.LOADING);
  const [lighthouseFilters, setLighthouseFilters] =
    useState<LighthouseFilters>(EmptyFilters);
  const [lighthouseFiltersState, setLighthouseFiltersState] = useState(
    DataState.LOADING
  );
  const SYNC_METRICS_DEFAULT = false;
  const [filterVerticalValue, setFilterVerticalValue] = useState('');
  const [verticalOptions, setVerticalOptions] = useState<string[]>([]);
  const [coachingDetailsVerticalOptions, setCoachingDetailsVerticalOptions] =
    useState<string[]>([]);
  const [verticalValue, setVerticalValue] = useState('');
  const [skillValue, setSkillValue] = useState('ALL');
  const [channelValue, setChannelValue] = useState('ALL');
  const [segmentValue, setSegmentValue] = useState('ALL');
  const [marketplaceValue, setMarketplaceValue] = useState('ALL');
  const [skillOptions, setSkillOptions] = useState<string[]>([]);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<string[]>([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState<string[]>([]);
  const [refreshGoalsFlag, setRefreshGoalsFlag] = useState(false);

  const compareByButtonGroupClickStreamContentId = `metrics-compare-data-by-${dateSelection}-button`;
  const categoryGroupClickStreamContentId = `metrics-compare-data-by-${metricCategory}-button`;
  const viewByGroupClickStreamContentId = `metrics-compare-data-by-${viewByType}-button`;
  const viewAsGroupClickStreamContentId = `metrics-compare-data-by-${currentTab}-tab`;
  const [
    DateSelectionButtonGroupClickStreamTrackingTag,
    emitDateSelectionClickstreamTags,
  ] = useCustomTracking(
    compareByButtonGroupClickStreamContentId,
    'metrics-slot',
    {
      'data-csa-c-type': 'button',
      'data-csa-c-content-id': compareByButtonGroupClickStreamContentId,
      'data-csa-c-tag1': 'Metrics',
      'data-csa-c-tag2': `metrics/${userId}`,
      'data-csa-c-profile-id': requesterUserID,
      'data-csa-c-username': requesterUsername,
      'data-csa-c-is-tm': isTm,
    }
  );

  const [
    CategorySelectionButtonGroupClickStreamTrackingTag,
    emitCategorySelectionClickstreamTags,
  ] = useCustomTracking(categoryGroupClickStreamContentId, 'metrics-slot', {
    'data-csa-c-type': 'button',
    'data-csa-c-content-id': categoryGroupClickStreamContentId,
    'data-csa-c-tag1': 'Metrics',
    'data-csa-c-tag2': `metrics/${userId}`,
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  });

  const [
    ViewBySelectionButtonGroupClickStreamTrackingTag,
    emitViewBySelectionClickstreamTags,
  ] = useCustomTracking(viewByGroupClickStreamContentId, 'metrics-slot', {
    'data-csa-c-type': 'button',
    'data-csa-c-content-id': viewByGroupClickStreamContentId,
    'data-csa-c-tag1': 'Metrics',
    'data-csa-c-tag2': `metrics/${userId}`,
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  });

  const [
    ViewAsSelectionButtonGroupClickStreamTrackingTag,
    emitViewAsSelectionClickstreamTags,
  ] = useCustomTracking(viewAsGroupClickStreamContentId, 'metrics-slot', {
    'data-csa-c-type': 'tab',
    'data-csa-c-content-id': viewAsGroupClickStreamContentId,
    'data-csa-c-tag1': 'Metrics',
    'data-csa-c-tag2': `metrics/${userId}`,
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  });

  const commonClickstreamDataForTrends = {
    'data-csa-c-slotid': 'metrics-slot',
    'data-csa-c-tag1': 'Metrics',
    'data-csa-c-tag2': `metrics/${userId}`,
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  };

  useEffect(() => {
    if (
      inspireOptions.state !== DataState.LOADED ||
      profileState !== DataState.LOADED
    ) {
      return;
    }
    if (userId && dateSelection) {
      const profileQuery = apiNaws.prepareQuery(
        TRAILING_DAYS,
        userId,
        cscEntityPrefix
      );
      apiNaws.getTeamProfileData(
        profileQuery,
        (prof: TeamProfile) => {
          setTeamProfile(prof);
        },
        (state: any) => {
          setTeamState(state);
          setTeamProfileState(state);
        }
      );
    }
  }, [inspireOptions.state]);

  useEffect(() => {
    if (teamProfile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }
    const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
      teamProfile.user.userID,
      cscEntityPrefix
    );

    apiNaws.getDefaultVertical(
      queryVariables,
      setLHDefaultVertical,
      (args: any) => {
        setLHDefaultVerticalState(args);
      }
    );
  }, [teamProfile]);

  useEffect(() => {
    if (teamProfile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    if (verticalValue === '') {
      return;
    }

    const mappedVerticalValue = mapCapAndConsumerToV2ForTrend(
      verticalValue,
      profile
    );

    const metricList = getMetricListForVertical(
      mappedVerticalValue as VerticalOption
    );

    // Trends Metrics
    const trendsMetricsCacheKey = getMetricsCacheKey();
    const cachedTrendsMetricsItem = trendsMetricsCache.find(
      item => item.key === trendsMetricsCacheKey
    );

    if (cachedTrendsMetricsItem && dateSelection !== DateCompare.CUSTOM) {
      setTrendsMetrics({ ...cachedTrendsMetricsItem.value });
      setTrendsMetricsState(DataState.LOADED);
    } else {
      const queryTrendsMetrics = apiNaws.prepareTrendsMetricsQuery(
        userId,
        cscEntityPrefix,
        getTeamDirectReports(teamProfile),
        mappedVerticalValue,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        convertDateCompareToTrendMetrics(dateSelection),
        convertDateCompareToTrendMetrics(dateSelection) === PresetDate.CUSTOM
          ? (dateRange ?? defaultDateRange)
          : [],
        metricList
      );
      apiNaws.getTrendsMetrics(
        queryTrendsMetrics,
        setTrendsMetrics,
        setTrendsMetricsState
      );
    }

    // Team Details Metrics
    const teamDetailsMetricsCacheKey = getMetricsCacheKey();
    const cachedTeamDetailsMetricsItem = teamDetailsMetricsCache.find(
      item => item.key === teamDetailsMetricsCacheKey
    );

    if (cachedTeamDetailsMetricsItem && dateSelection !== DateCompare.CUSTOM) {
      setTeamDetailsMetrics(cachedTeamDetailsMetricsItem.value);
      setTeamDetailsMetricsState(DataState.LOADED);
    } else {
      const queryTeamDetailsMetrics = apiNaws.prepareTeamDetailsMetricsQuery(
        userId,
        cscEntityPrefix,
        getReportingTeamForDirectReports(teamProfile.reportingTeam),
        mappedVerticalValue,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        convertDateCompareToTrendMetrics(dateSelection),
        convertDateCompareToTrendMetrics(dateSelection) === PresetDate.CUSTOM
          ? (dateRange ?? defaultDateRange)
          : [],
        metricList
      );

      apiNaws.getTeamDetailsMetrics(
        queryTeamDetailsMetrics,
        setTeamDetailsMetrics,
        setTeamDetailsMetricsState
      );
    }
  }, [
    dateSelection,
    teamProfile,
    verticalValue,
    marketplaceValue,
    skillValue,
    channelValue,
    segmentValue,
    dateRange,
  ]);

  useEffect(() => {
    if (teamProfile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }
    if (dateSelection) {
      const { startDate, endDate } = getDateRange(
        dateSelection,
        defaultDateRange[0],
        defaultDateRange[1]
      );
      const queryVariablesCustomFilters =
        apiNaws.prepareLighthouseCustomFiltersQuery(
          userId,
          cscEntityPrefix,
          true,
          startDate,
          endDate,
          getTeamDirectReports(teamProfile),
          getTeamIsTMForDirectReports(teamProfile),
          SYNC_METRICS_DEFAULT
        );
      apiNaws.getLighthouseCustomFilters(
        queryVariablesCustomFilters,
        setLighthouseFilters,
        setLighthouseFiltersState
      );
    }
  }, [dateSelection, teamProfile, dateRange]);

  useEffect(() => {
    if (teamProfile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    if (verticalValue !== '') {
      let newMetricMap: string[] = [];
      const mappedVerticalValue = mapCapAndConsumerToV2ForTrend(
        verticalValue,
        profile
      );
      const trendMapForVertical = trendMetricsMap[mappedVerticalValue];
      if (trendMapForVertical) {
        const trendMapForVerticalCategory =
          trendMapForVertical[
            TrendCategory[metricCategory as keyof typeof TrendCategory]
          ];
        for (const option in trendMapForVerticalCategory) {
          newMetricMap.push(option);
        }
        newMetricMap = filterCCXMetricsForRemoval(
          filterCCXNeg(newMetricMap)
        ).sort();
        setMetricTypeOptionsList(newMetricMap);
        setMetricType(newMetricMap);
      }
    }
  }, [verticalValue, metricCategory]);

  useEffect(() => {
    if (teamProfile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    if (verticalValue !== '') {
      let newMetricCategoryList: string[] = [];
      const trendMapForVertical = trendMetricsMap[verticalValue];
      for (const category in trendMapForVertical) {
        if (Object.keys(trendMapForVertical[category]).length !== 0) {
          newMetricCategoryList.push(category);
        }
      }
      const sortedList: string[] = [];
      const keys = Object.keys(TrendCategory);
      for (const key of keys) {
        if (newMetricCategoryList.indexOf(key) >= 0) {
          sortedList.push(key);
        }
      }
      if (sortedList.length > 0 && sortedList.indexOf(metricCategory) < 0) {
        setMetricCategory(sortedList[0] as TrendCategory);
      }
      setMetricCategoryList(sortedList);
    }
  }, [verticalValue]);

  useEffect(() => {
    if (teamProfile.user.userID === '' || profileState === DataState.LOADING) {
      return;
    }

    if (verticalValue !== '') {
      let newSubMetricMap: string[] = [];
      const trendMapForVertical = trendMetricsMap[verticalValue];
      if (trendMapForVertical) {
        const trendMapForVerticalCategory =
          trendMapForVertical[
            TrendCategory[metricCategory as keyof typeof TrendCategory]
          ];
        metricType.forEach(metric => {
          if (trendMapForVerticalCategory[metric]) {
            trendMapForVerticalCategory[metric].forEach(subOption => {
              newSubMetricMap.push(subOption);
            });
          }
        });
        newSubMetricMap = newSubMetricMap.sort();
        setSubMetricType(newSubMetricMap);
        newSubMetricMap.length === 0
          ? setNoSubmetricOptions(true)
          : setNoSubmetricOptions(false);
        setSubMetricTypeOptionsList(newSubMetricMap);
      }
    }
  }, [metricType]);

  useEffect(() => {
    if (!trendsMetrics) {
      return;
    }
    const trendsMetricsDate = convertDateCompareToTrendMetrics(dateSelection);
    const trendsMetricsCacheKey = getMetricsCacheKey();

    setTrendsMetricsCache(trendsMetricsCache => {
      const itemExists = trendsMetricsCache.some(
        cacheItem => cacheItem.key === trendsMetricsCacheKey
      );
      if (itemExists) {
        return trendsMetricsCache;
      }

      const newTrendsMetricsCache = [
        ...trendsMetricsCache,
        { key: trendsMetricsCacheKey, value: trendsMetrics },
      ];
      if (newTrendsMetricsCache.length > 20) {
        newTrendsMetricsCache.shift();
      }
      return newTrendsMetricsCache;
    });
  }, [trendsMetrics]);

  useEffect(() => {
    if (!teamDetailsMetrics) {
      return;
    }
    const metricsDate = convertDateCompareToTrendMetrics(dateSelection);
    const cacheKey = getMetricsCacheKey();

    setTeamDetailsMetricsCache(teamDetailsMetricsCache => {
      const itemExists = teamDetailsMetricsCache.some(
        cacheItem => cacheItem.key === cacheKey
      );
      if (itemExists) {
        return teamDetailsMetricsCache;
      }
      const newTeamDetailsMetricsCache = [
        ...teamDetailsMetricsCache,
        { key: cacheKey, value: teamDetailsMetrics },
      ];
      if (newTeamDetailsMetricsCache.length > 20) {
        newTeamDetailsMetricsCache.shift();
      }
      return newTeamDetailsMetricsCache;
    });
  }, [teamDetailsMetrics]);

  useEffect(() => {
    if (!teamDetailsMetrics) {
      return;
    }
    setDateList(getTimeOptions(teamDetailsMetrics));
  }, [teamDetailsMetrics]);

  const isGoUser = checkGoUser(profile.user.owner);
  const enableRAPRemovalAndCCXUpdate = isFeatureEnabled(
    Feature.ENABLE_RAP_REMOVAL_AND_CCX_UPDATE
  );

  const enableTrendMetrics = isFeatureEnabled(Feature.TREND_METRICS);

  useEffect(() => {
    if (
      dateSelection === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    setSkillOptions(lighthouseFilters.skills);
    setChannelOptions(lighthouseFilters.channels);
    setSegmentOptions(lighthouseFilters.segments);
    setMarketplaceOptions(lighthouseFilters.marketplaces ?? []);

    const verticalParamCoachingDetails = lighthouseFilters.verticals.filter(
      vertical =>
        vertical !== VerticalOption.Total_V2 &&
        vertical !== VerticalOption.Total &&
        vertical !== VerticalOption.Total_GO_V2 &&
        vertical !== VerticalOption.Total_CSC
    );

    const tempVertical = [...verticalParamCoachingDetails];
    const sortedVertical = [...new Set(tempVertical)];

    setCoachingDetailsVerticalOptions(sortedVertical);

    const sortedVerticals = sortVerticalsAndPutTotalFirst(
      lighthouseFilters.verticals
    );
    setVerticalOptions(sortedVerticals);
    if (filterVerticalValue === '') {
      LHDefaultVertical === null
        ? setFilterVerticalValue(verticalParamFromARC ?? sortedVerticals[0])
        : setFilterVerticalValue(verticalParamFromARC ?? LHDefaultVertical);
    }
  }, [lighthouseFiltersState, LHDefaultVerticalState, dateSelection]);

  useEffect(() => {
    if (filterVerticalValue === '' || filterVerticalValue === undefined) return;
    setVerticalValue(filterVerticalValue);
  }, [
    filterVerticalValue,
    isGoUser,
    LHDefaultVertical,
    enableRAPRemovalAndCCXUpdate,
  ]);

  useEffect(() => {
    if (verticalValue === '') return;
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
  }, [verticalValue]);

  const csaListSheetRef = useRef<CSASheetRef>(null);

  function updateDateRange(newDateRange: any[]) {
    if (newDateRange[0] && newDateRange[1]) {
      setDateRange(newDateRange); // Only set the dateRange variable if we have non-null start and end dates.
    }
  }

  function changeDateSelection(newDateSelection: string) {
    if (csaListSheetRef.current) {
      csaListSheetRef.current.hideSheet();
    }
    setDateSelection(newDateSelection);
    setSheetVisible(false);
    emitDateSelectionClickstreamTags();
  }

  function changeMetricCategorySelection(newMetricCategory: TrendCategory) {
    setMetricCategory(newMetricCategory);
    emitCategorySelectionClickstreamTags();
  }

  function changeCurrentTab(newTab: string) {
    setCurrentTab(newTab);
    emitViewAsSelectionClickstreamTags();
  }

  function changeViewByType(newViewBy: ViewByToggle) {
    setViewByType(newViewBy);
    emitViewBySelectionClickstreamTags();
  }

  const handleSheetVisibilityChange = (sheetVisible: boolean) => {
    setSheetVisible(sheetVisible);
  };

  return (
    <Box>
      <Responsive
        query='min-width'
        props={{
          isBig: {
            default: false,
            '1360px': true,
          },
        }}
      >
        {({ isBig }): React.ReactElement => (
          <>
            <Row
              width='100%'
              widths={['fill', 'fit']}
              alignmentVertical='top'
              height='100%'
            >
              <Box height='100%'>
                {profileState === DataState.LOADING &&
                  (filterVerticalValue !== '' ||
                    filterVerticalValue !== undefined) && (
                    <div style={{ textAlign: 'center' }}>
                      <Loader type='circular' />
                    </div>
                  )}
                {profileState === DataState.LOADED && !isOm && (
                  <TeamProfileContainer
                    state={profileState}
                    profile={teamProfile}
                    selectedWeek={weekValue}
                    setWeek={setWeek}
                    isBig={isBig}
                  />
                )}
                {profileState === DataState.ERROR && (
                  <Alert type='error'>
                    <FormattedMessage
                      id='cs_perfportal_error_reason_1'
                      defaultMessage='Your credentials may be incorrect or inadequate.'
                    />
                  </Alert>
                )}
                <Row width='100%' widths='fill' alignmentVertical='center'>
                  <Box spacingInset='none none 400' data-cy='team-performance'>
                    <Heading level={1} type='h600'>
                      {isOm ? (
                        <FormattedMessage
                          id='cs_perfportal_tm_trends_over_time'
                          defaultMessage='TM Trends Over Time'
                        />
                      ) : (
                        <FormattedMessage
                          id='cs_perfportal_metrics'
                          defaultMessage='Metrics'
                        />
                      )}
                    </Heading>
                  </Box>
                  {/* Render the Metrics header */}
                  <Row
                    alignmentHorizontal='end'
                    alignmentVertical='center'
                    spacing='300'
                  >
                    <Text type='h200'>
                      <FormattedMessage
                        id='cs_perfportal_metrics_compare_data_by'
                        defaultMessage='Compare Data By'
                      />
                    </Text>
                    <span
                      style={{ display: 'inline-block', width: '2px' }}
                    ></span>
                    <DateSelectionButtonGroupClickStreamTrackingTag />
                    <CategorySelectionButtonGroupClickStreamTrackingTag />
                    <ViewBySelectionButtonGroupClickStreamTrackingTag />
                    <ViewAsSelectionButtonGroupClickStreamTrackingTag />
                    <ButtonGroup
                      value={dateSelection}
                      onChange={changeDateSelection}
                    >
                      <ButtonOption value={DateCompare.DAY} data-cy='day'>
                        <FormattedMessage
                          id='cs_perfportal_day'
                          defaultMessage='Day'
                        />
                      </ButtonOption>
                      <ButtonOption value={DateCompare.WEEK} data-cy='week'>
                        <FormattedMessage
                          id='cs_perfportal_week'
                          defaultMessage='Week'
                        />
                      </ButtonOption>
                      <ButtonOption value={DateCompare.MONTH} data-cy='month'>
                        <FormattedMessage
                          id='cs_perfportal_month'
                          defaultMessage='Month'
                        />
                      </ButtonOption>
                      <ButtonOption value={DateCompare.CUSTOM} data-cy='custom'>
                        <FormattedMessage
                          id='cs_perfportal_custom'
                          defaultMessage='Custom'
                        />
                      </ButtonOption>
                    </ButtonGroup>
                  </Row>
                </Row>
                {enableAssociateRichCards && !isOm && (
                  <BottomQuartile
                    teamProfile={teamProfile}
                    teamProfileState={teamProfileState}
                    vertical={LHDefaultVertical}
                  />
                )}
                {enableTrendMetrics && (
                  <Row
                    className={styles.filterContainer}
                    width='100%'
                    wrap='down'
                    alignmentVertical='center'
                    alignmentHorizontal='justify'
                  >
                    <Box type='fill' spacingInset='400' width='100%'>
                      {lighthouseFiltersState === DataState.LOADING && (
                        <div style={{ textAlign: 'center' }}>
                          <Loader type='circular' size='large' />
                        </div>
                      )}
                      {lighthouseFiltersState === DataState.LOADED &&
                        LHDefaultVerticalState === DataState.LOADED && (
                          <>
                            <Box
                              spacingInset='200'
                              className={styles.filterDropdown}
                            >
                              <Text type='h200'>
                                <FormattedMessage
                                  id='cs_perfportal_metrics_filter_by_attribute'
                                  defaultMessage='Filter by attributes'
                                />
                              </Text>
                            </Box>
                            <Row
                              width='100%'
                              wrap='down'
                              alignmentVertical='center'
                              alignmentHorizontal='justify'
                              spacingInset='none none 400 none'
                            >
                              {dateSelection === DateCompare.CUSTOM && (
                                <DateRangePicker
                                  value={dateRange}
                                  onChange={(values: any) => {
                                    updateDateRange(values);
                                  }}
                                  disabledDates={isDateValid}
                                  startLabel={intl.formatMessage({
                                    id: 'cs_perfportal_metrics_custom_start_date',
                                    defaultMessage: 'Start date',
                                  })}
                                  endLabel={intl.formatMessage({
                                    id: 'cs_perfportal_metrics_custom_end_date',
                                    defaultMessage: 'End date',
                                  })}
                                  monthsInView={2}
                                  startError={!dateRange[0]}
                                  endError={!dateRange[1]}
                                  size='xlarge'
                                  width={400}
                                />
                              )}
                              <Filters
                                verticalValue={filterVerticalValue}
                                skillValue={skillValue}
                                channelValue={channelValue}
                                segmentValue={segmentValue}
                                marketplaceValue={marketplaceValue}
                                verticalOptions={verticalOptions}
                                skillOptions={skillOptions}
                                channelOptions={channelOptions}
                                segmentOptions={segmentOptions}
                                marketplaceOptions={marketplaceOptions}
                                setMarketplaceValue={setMarketplaceValue}
                                setVerticalValue={setFilterVerticalValue}
                                setSkillValue={setSkillValue}
                                setChannelValue={setChannelValue}
                                setSegmentValue={setSegmentValue}
                                weekValue={''}
                                filterTruncate={undefined}
                                weekOptions={[]}
                                dateRange={undefined}
                                customDate={undefined}
                                weekValueChanger={undefined}
                                setCustomDate={undefined}
                                setDateRange={undefined}
                                page={Page.METRICS}
                              />
                            </Row>
                            <Box
                              spacingInset='none 200 none none'
                              className={styles.filterDropdown}
                            >
                              <Text type='h200'>
                                <FormattedMessage
                                  id='cs_perfportal_metrics_filter_by_metrics'
                                  defaultMessage='Filter by metrics'
                                />
                              </Text>
                            </Box>
                            <Row>
                              <Box>
                                <InputGroup size='xlarge' direction='column'>
                                  <Select
                                    label={intl.formatMessage({
                                      id: 'cs_perfportal_metrics_metric_category',
                                      defaultMessage: 'Metric Category',
                                    })}
                                    value={metricCategory}
                                    onChange={changeMetricCategorySelection}
                                    width={250}
                                    size='xlarge'
                                  >
                                    {metricCategoryList.map(metricCategory => {
                                      return (
                                        <SelectOption
                                          value={metricCategory}
                                          label={intl.formatMessage(
                                            getTrendCategoryStringIdAndDefaultMessage(
                                              TrendCategory[
                                                metricCategory as keyof typeof TrendCategory
                                              ]
                                            )
                                          )}
                                          key={metricCategory}
                                          {...commonClickstreamDataForTrends}
                                          data-csa-c-content-id='metrics-category-select'
                                          data-csa-c-type='select'
                                          data-csa-c-tag3={metricCategory}
                                        />
                                      );
                                    })}
                                  </Select>
                                </InputGroup>
                              </Box>
                              <Box>
                                <InputGroup size='xlarge' direction='column'>
                                  <Select
                                    label={intl.formatMessage({
                                      id: 'cs_perfportal_metrics_metric_type',
                                      defaultMessage: 'Metric Type',
                                    })}
                                    value={metricType}
                                    onChange={current => setMetricType(current)}
                                    width={250}
                                    data-cy='metric_type'
                                    size='xlarge'
                                  >
                                    {metricTypeOptionsList.map(metricOption => {
                                      if (
                                        !enableHighSilence &&
                                        metricOption ===
                                          MetricsOption.HIGH_SILENCE_RATE
                                      )
                                        return <></>;
                                      return (
                                        <SelectOption
                                          value={metricOption}
                                          label={intl.formatMessage(
                                            getMetricStringIdAndDefaultMessage(
                                              metricOption
                                            )
                                          )}
                                          key={metricOption}
                                          {...commonClickstreamDataForTrends}
                                          data-csa-c-content-id='metrics-type-select'
                                          data-csa-c-type='select'
                                          data-csa-c-tag3={metricOption}
                                        />
                                      );
                                    })}
                                  </Select>
                                </InputGroup>
                              </Box>
                              <Box>
                                <InputGroup size='xlarge' direction='column'>
                                  <Select
                                    label={intl.formatMessage({
                                      id: 'cs_perfportal_metrics_submetrics',
                                      defaultMessage: 'Submetrics',
                                    })}
                                    value={subMetricType}
                                    onChange={current =>
                                      setSubMetricType(current)
                                    }
                                    width={250}
                                    disabled={noSubmetricOptions}
                                    size='xlarge'
                                  >
                                    {subMetricTypeOptionsList.map(
                                      submetricOptions => {
                                        return (
                                          <SelectOption
                                            value={submetricOptions}
                                            label={intl.formatMessage(
                                              getMetricStringIdAndDefaultMessage(
                                                submetricOptions
                                              )
                                            )}
                                            key={submetricOptions}
                                            {...commonClickstreamDataForTrends}
                                            data-csa-c-content-id='submetrics-type-select'
                                            data-csa-c-type='select'
                                            data-csa-c-tag3={submetricOptions}
                                          />
                                        );
                                      }
                                    )}
                                  </Select>
                                </InputGroup>
                              </Box>
                            </Row>
                          </>
                        )}
                    </Box>
                  </Row>
                )}
                {trendsMetricsState === DataState.LOADING &&
                  filterVerticalValue !== '' &&
                  filterVerticalValue !== undefined && (
                    <div style={{ textAlign: 'center' }}>
                      <Loader type='circular' />
                    </div>
                  )}
                {trendsMetricsState === DataState.LOADED && trendsMetrics && (
                  <>
                    <Row
                      width='100%'
                      widths='fill'
                      alignmentHorizontal='end'
                      alignmentVertical='center'
                    >
                      <Box spacingInset='400' minHeight={60}>
                        {!isOm && (
                          <Text type='h500'>
                            <FormattedMessage
                              id='cs_perfportal_metrics_trends_over_time'
                              defaultMessage='Trends Over Time'
                            />
                          </Text>
                        )}
                      </Box>
                    </Row>
                    <Column>
                      <Row spacingInset='300' minHeight={60}>
                        <TabGroup
                          value={currentTab}
                          onChange={changeCurrentTab}
                          aria-labelledby='tablist-heading'
                        >
                          <Tab
                            value='teamview'
                            aria-controls='tabpanel-teamview'
                            data-cy='tabpanel-teamview'
                          >
                            <FormattedMessage
                              id='cs_perfportal_metrics_team_view'
                              defaultMessage='Team View'
                            />
                          </Tab>
                          <Tab
                            value='csaview'
                            aria-controls='tabpanel-csaview'
                            data-cy='tabpanel-csaview'
                          >
                            <FormattedMessage
                              id='cs_perfportal_metrics_view'
                              defaultMessage='{directReportType} View'
                              values={{ directReportType }}
                            />
                          </Tab>
                        </TabGroup>
                        <Row minWidth={200} minHeight={60}>
                          <Text type='h100'>
                            <FormattedMessage
                              id='cs_perfportal_metrics_view_by'
                              defaultMessage='View By'
                            />
                          </Text>
                          <ButtonGroup
                            value={viewByType}
                            onChange={changeViewByType}
                            size='small'
                          >
                            <ButtonOption
                              value={ViewByToggle.GRAPH}
                              data-cy='graph'
                            >
                              <FormattedMessage
                                id='cs_perfportal_metrics_graph'
                                defaultMessage='Graph'
                              />
                            </ButtonOption>
                            <ButtonOption
                              value={ViewByToggle.TABLE}
                              data-cy='table'
                            >
                              <FormattedMessage
                                id='cs_perfportal_metrics_table'
                                defaultMessage='Table'
                              />
                            </ButtonOption>
                          </ButtonGroup>
                        </Row>
                      </Row>
                    </Column>
                    {currentTab === 'teamview' &&
                      (viewByType === ViewByToggle.GRAPH ? (
                        <>
                          {trendsMetricsState === DataState.LOADING && (
                            <div style={{ textAlign: 'center' }}>
                              <Loader type='circular' />
                            </div>
                          )}
                          {trendsMetricsState === DataState.LOADED && (
                            <TeamGraphView
                              dateSelection={dateSelection}
                              trendAndDetailsMetrics={filterMetricsAndSubmetrics(
                                trendsMetrics,
                                verticalValue,
                                metricCategory,
                                metricType,
                                subMetricType
                              )}
                              metricCategory={metricCategory}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {trendsMetricsState === DataState.LOADING && (
                            <div style={{ textAlign: 'center' }}>
                              <Loader type='circular' />
                            </div>
                          )}
                          {trendsMetricsState === DataState.LOADED && (
                            <TeamView
                              dateSelection={dateSelection}
                              trendMetrics={filterMetricsAndSubmetrics(
                                trendsMetrics,
                                verticalValue,
                                metricCategory,
                                metricType,
                                subMetricType
                              )}
                              metricType={metricType}
                              subMetricType={subMetricType}
                              metricCategory={metricCategory}
                              customDate={dateSelection === DateCompare.CUSTOM}
                            ></TeamView>
                          )}
                        </>
                      ))}
                    {currentTab === 'csaview' &&
                      (viewByType === ViewByToggle.GRAPH ? (
                        <>
                          {teamDetailsMetricsState === DataState.LOADING && (
                            <div style={{ textAlign: 'center' }}>
                              <Loader type='circular' />
                            </div>
                          )}
                          {teamDetailsMetricsState === DataState.LOADED &&
                            teamDetailsMetrics && (
                              <CSAGraphView
                                dateSelection={dateSelection}
                                trendAndDetailsMetrics={filterMetricsAndSubmetrics(
                                  teamDetailsMetrics,
                                  verticalValue,
                                  metricCategory,
                                  metricType,
                                  subMetricType
                                )}
                                metricCategory={metricCategory}
                                teamProfile={teamProfile}
                                clickstream={commonClickstreamDataForTrends}
                              />
                            )}
                        </>
                      ) : (
                        <>
                          {teamDetailsMetricsState === DataState.LOADING && (
                            <div style={{ textAlign: 'center' }}>
                              <Loader type='circular' />
                            </div>
                          )}
                          {teamDetailsMetricsState === DataState.LOADED &&
                            teamDetailsMetrics && (
                              <CSAView
                                dateSelection={dateSelection}
                                showNetworkAverage={true}
                                teamProfile={teamProfile}
                                dateList={dateList}
                                teamDetailsMetricsData={filterMetricsAndSubmetrics(
                                  teamDetailsMetrics,
                                  verticalValue,
                                  metricCategory,
                                  metricType,
                                  subMetricType
                                )}
                                metricType={metricType}
                                subMetricType={subMetricType}
                                verticalValue={verticalValue}
                                marketplaceValue={marketplaceValue}
                                skillValue={skillValue}
                                channelValue={channelValue}
                                segmentValue={segmentValue}
                                metricCategory={metricCategory}
                                clickstream={commonClickstreamDataForTrends}
                                customDate={dateSelection === PresetDate.CUSTOM}
                              ></CSAView>
                            )}
                        </>
                      ))}
                  </>
                )}
                {enableGoalSetting && !isOm && (
                  <GoalSettingContainerWrapper
                    loading={profileState === DataState.LOADING}
                    loaded={profileState === DataState.LOADED}
                    error={profileState === DataState.ERROR}
                    displayedProfile={profile}
                    loggedInProfile={profile}
                    refreshGoalsFlag={refreshGoalsFlag}
                    page={GoalPage.METRICS}
                  />
                )}
              </Box>
              {!isOm && (
                <CSAList
                  csas={csaListSheetData.csas}
                  label={csaListSheetData.label}
                  clickstream={csaListSheetData.clickstream}
                  onSheetVisibilityChange={handleSheetVisibilityChange}
                  ref={csaListSheetRef}
                ></CSAList>
              )}
            </Row>
          </>
        )}
      </Responsive>
    </Box>
  );
};

export default TimeTrendsAndMetricsTeam;
